import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useRotationsplanFeatureContext } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/RotationsplanFeatureContext';
import { faXmarkLarge } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Abschnitt = { value: string; name: string };

// eslint-disable-next-line max-lines-per-function
export function LehrjahrFilter() {
  const { t } = useTranslation();
  const { store } = useRotationsplanFeatureContext();
  const {
    ui: {
      kalender: {
        filter: { abschnitte: abschnittFilter },
      },
    },
  } = store;
  const [filter, setFilter] = useState('');
  const [selectedAbschnitte, setSelectedAnschnitte] =
    useState<Abschnitt[]>(abschnittFilter);

  function onChangeSelectedAbschnitt(abschnitt: Abschnitt) {
    if (
      selectedAbschnitte
        .map((abschnitt) => abschnitt.value)
        .includes(abschnitt.value)
    ) {
      const newAbschnitte = selectedAbschnitte.filter(
        (beruf) => beruf.value !== abschnitt.value,
      );
      setSelectedAnschnitte(newAbschnitte);
      store.setGliederungFilter(newAbschnitte);
    } else {
      setSelectedAnschnitte([...selectedAbschnitte, abschnitt]);
      store.setGliederungFilter([...selectedAbschnitte, abschnitt]);
    }
  }

  function deselectAll() {
    setSelectedAnschnitte([]);
    store.setGliederungFilter([]);
  }

  function onChangeFilter(input: string) {
    setFilter(input);
  }

  return (
    <>
      <Popover>
        <PopoverTrigger className="hidden md:flex" asChild>
          <FilterTrigger
            selectedAbschnitte={selectedAbschnitte}
            deselectAll={deselectAll}
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="bg-white p-2 px-0 w-full md:max-w-70">
          <Command>
            <CommandInput
              placeholder={t('domain.mitarbeiter.rotationsplan.filter.suchen')}
            />
            <CommandList>
              {store.data.abschnitte.map((abschnitt) => (
                <CommandItem
                  key={abschnitt.value}
                  value={abschnitt.name + '%' + abschnitt.value}
                  onSelect={(value) =>
                    onChangeSelectedAbschnitt({
                      name: value.split('%')[0],
                      value: value.split('%')[1],
                    })
                  }>
                  <ListItem
                    selectedAbschnitte={selectedAbschnitte}
                    abschnitt={abschnitt}
                    onChangeSelectedAbschnitt={onChangeSelectedAbschnitt}
                  />
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Drawer>
        <DrawerTrigger className="flex md:hidden" asChild>
          <FilterTrigger
            selectedAbschnitte={selectedAbschnitte}
            deselectAll={deselectAll}
          />
        </DrawerTrigger>
        <DrawerContent className="flex flex-col gap-4 p-4">
          <div className="font-semibold text-center">
            {t('domain.mitarbeiter.rotationsplan.filter.lehrjahr')}
          </div>
          <Input
            defaultValue={filter}
            placeholder={t('domain.mitarbeiter.rotationsplan.filter.suchen')}
            onChange={(event) => onChangeFilter(event.target.value)}
          />
          <ScrollArea className="overflow-auto">
            <div className="flex flex-col gap-2">
              {store.data.abschnitte
                .filter((abschnitt) =>
                  abschnitt.name
                    .toLocaleLowerCase()
                    .includes(filter.toLocaleLowerCase()),
                )
                .map((abschnitt, index) => (
                  <div
                    className="flex flex-col gap-2"
                    key={abschnitt.value}
                    onClick={() => onChangeSelectedAbschnitt(abschnitt)}>
                    <ListItem
                      selectedAbschnitte={selectedAbschnitte}
                      abschnitt={abschnitt}
                      onChangeSelectedAbschnitt={onChangeSelectedAbschnitt}
                    />
                    {index < store.data.abschnitte.length - 1 && (
                      <div className="w-full bg-neutrals-200 h-px"></div>
                    )}
                  </div>
                ))}
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    </>
  );
}

function FilterTrigger({
  selectedAbschnitte,
  deselectAll,
  ...props
}: {
  selectedAbschnitte: Abschnitt[];
  deselectAll: () => void;
} & HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslation();
  return (
    <ButtonGroup {...props}>
      <Button className="flex items-center gap-2" variant="outline-secondary">
        {t('domain.mitarbeiter.rotationsplan.filter.lehrjahr')}
        {selectedAbschnitte.length > 0 && (
          <Badge type="pill" variant="secondary">
            {selectedAbschnitte.length}
          </Badge>
        )}
      </Button>
      {selectedAbschnitte.length > 0 && (
        <Button
          variant="outline-secondary"
          onClick={(e) => {
            e.preventDefault();
            deselectAll();
          }}>
          <FontAwesomeIcon icon={faXmarkLarge} />
        </Button>
      )}
    </ButtonGroup>
  );
}

export function ListItem({
  selectedAbschnitte,
  abschnitt,
  onChangeSelectedAbschnitt,
}: {
  selectedAbschnitte: Abschnitt[];
  abschnitt: Abschnitt;
  onChangeSelectedAbschnitt: (abschnitt: Abschnitt) => void;
}) {
  return (
    <div className="flex justify-between items-center w-full font-normal">
      <div className="whitespace-nowrap truncate">{abschnitt.name}</div>
      <Checkbox
        checked={selectedAbschnitte
          .map((abschnitt) => abschnitt.value)
          .includes(abschnitt.value)}
        onChange={() => onChangeSelectedAbschnitt(abschnitt)}
        className="disabled:opacity-100 disabled:cursor-auto"
      />
    </div>
  );
}
